import type { FC } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { useLocation } from "react-router-dom";
import Divider from "@mui/material/Divider";
import AppLogo from "../AppLogo";
import I18nSelect from "../@common/I18nSelect";
import {
  FaSquareFacebook,
  FaLinkedin,
  FaSquareXTwitter,
  FaInstagram,
  FaRegAddressBook,
} from "react-icons/fa6";
import { useLanguageTranslator } from "../useLanguageContext";
import { MdOutlineEmail, MdOutlinePhone } from "react-icons/md";

const FooterPublic: FC = () => {
  const location = useLocation();
  const { translator } = useLanguageTranslator();
  const currentYear = new Date().getFullYear();

  const pages = [
    {
      title: translator("navbar.Home"),
      path: location?.pathname === "/" ? "#" : "/",
    },
    {
      title: translator("navbar.About Us"),
      path: location?.pathname === "/" ? "#about-us" : "/",
    },
    {
      title: translator("navbar.Our Focus"),
      path: location?.pathname === "/" ? "#our-focus" : "/",
    },
    {
      title: translator("navbar.FAQ"),
      path: location?.pathname === "/" ? "#FAQ" : "/",
    },
    {
      title: translator("landingPage.Navigating the Niyel Tracker"),
      path: location?.pathname === "/" ? "#step-by-step-guide" : "/",
    },
  ];

  return (
    <Paper component={Container} square>
      <Stack
        direction={{ lg: "row" }}
        alignItems={{ xs: "center", lg: "flex-start" }}
        gap={3}
        sx={{ pt: 3, pb: 10 }}
      >
        <Box sx={{ flex: 2, textAlign: { xs: "center", lg: "left" } }}>
          <AppLogo size="small" />
          <Typography color="textSecondary" sx={{ mt: 1, ml: 1 }}>
            {translator(
              "landingPage.The first African tracking tool to support your advocacy management"
            )}{" "}
            {translator("landingPage.with precise, real-time data.")}
          </Typography>
        </Box>
        <Box sx={{ flex: 3 }} />
        <Box sx={{ flex: 4 }}>
          <Stack
            direction={{ md: "row" }}
            gap={{ xs: 5, lg: 10 }}
            justifyContent="flex-end"
          >
            <Stack gap={1} alignItems={{ xs: "center", lg: "flex-start" }}>
              <Typography color="textSecondary" mb={2}>
                {translator("Company")}
              </Typography>
              {pages.map((page) => (
                <Link
                  key={page?.title}
                  href={page?.path}
                  underline="none"
                  style={{
                    display: "block",
                    color:
                      location.hash === page.path ||
                      (!location.hash && page.path === "#")
                        ? "#00AAB0"
                        : "inherit",
                    fontWeight:
                      location.hash === page.path ||
                      (!location.hash && page.path === "#")
                        ? "bold"
                        : "normal",
                  }}
                  color="inherit"
                >
                  {page?.title}
                </Link>
              ))}
            </Stack>

            <Stack gap={1} alignItems={{ xs: "center", lg: "flex-start" }}>
              <Typography color="textSecondary" mb={2}>
                {translator("Contact")}
              </Typography>

              <Typography>
                <FaRegAddressBook
                  style={{ marginRight: "5px", marginTop: "5px" }}
                />
                Niyel Sénégal (Head Office),
                <br /> Yoff Virage Toundoup Rya, <br /> Lot 85 TF 5425. <br />{" "}
                {translator("Box")}: 10607 Dakar, Senegal.
              </Typography>
              <Typography sx={{ display: "flex", alignItems: "center" }}>
                <MdOutlineEmail style={{ marginRight: "5px" }} /> info@niyel.net
              </Typography>
              <Typography sx={{ display: "flex", alignItems: "center" }}>
                <MdOutlinePhone style={{ marginRight: "5px" }} /> (+221) 33 820
                50 93
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
      <Divider sx={{ opacity: 0.75 }} />
      <Stack
        direction={{ lg: "row" }}
        gap={5}
        alignItems="center"
        sx={{ py: 3 }}
      >
        <Box>
          <Typography variant="caption">
            {translator("Copyright")} &copy; {currentYear} The Niyel Tracker -{" "}
            {translator("All rights reserved")}.
          </Typography>
        </Box>
        <Stack direction="row" gap={3}>
          <Link
            href="/privacy-policy"
            underline="hover"
            variant="caption"
            color="inherit"
          >
            {translator("Privacy Policy")}
          </Link>
          <Link
            href="/terms-conditions"
            underline="hover"
            variant="caption"
            color="inherit"
          >
            {translator("Terms")} &amp; {translator("Conditions")}
          </Link>
        </Stack>
        <Box flexGrow={1} />
        <Stack
          direction="row"
          gap={3}
          alignItems="center"
          sx={{ color: "#878787" }}
        >
          <Link
            href="https://web.facebook.com/NiyelCampaigns?locale=fr_FR"
            target="_BLANK"
            underline="none"
            color="inherit"
          >
            <FaSquareFacebook size={32} />
          </Link>
          <Link
            href="https://www.linkedin.com/company/niyel/?originalSubdomain=sn"
            target="_BLANK"
            underline="none"
            color="inherit"
          >
            <FaLinkedin size={32} />
          </Link>
          <Link
            href="https://twitter.com/NiyelCampaigns"
            target="_BLANK"
            underline="none"
            color="inherit"
          >
            <FaSquareXTwitter size={32} />
          </Link>
          <Link
            href="https://www.instagram.com/niyelcampaigns/?hl=fr"
            target="_BLANK"
            underline="none"
            color="inherit"
          >
            <FaInstagram size={32} />
          </Link>
          {/* <I18nSelect
            options={[
              { label: translator("English"), value: "en" },
              { label: translator("French"), value: "fr" },
              { label: translator("Portuguese"), value: "pt" },
            ]}
          /> */}
        </Stack>
      </Stack>
    </Paper>
  );
};

export { FooterPublic };
